import {
  AppealStatus,
  TAppealOffer,
  TServiceOffer,
  TServiceOfferPrice,
} from 'feature/appeals/type'
import { checkIfDateLessTodayWithTime } from 'feature/appeals/helpers/checkIfDateLessToday'
import { getTimeByTimeZone } from 'helpers/date/getTimeByTimeZone'

// прогресс оплаты конкретного КП: одним платежом (FULL) либо по стадиям рассрочки
export type TOfferPaymentStage =
  | 'FULL'
  | 'AWAITING_FIRST_PAYMENT'
  | 'AWAITING_SECOND_LINK'
  | 'AWAITING_SECOND_PAYMENT'
  | 'SETTLED'

export type TOfferPriceBlockResult = {
  unpaidPayment: TServiceOfferPrice | undefined
  isPartialPayment: boolean
  payUrl: string | undefined
  stage: TOfferPaymentStage
  showPayButton: boolean
  stageStatusName: string | null
  isCheckButtonVisible: boolean
  createdDate: string
  expireDate: string
  isOfferActive: boolean
  isDeclined: boolean
}

const defaultResult: TOfferPriceBlockResult = {
  unpaidPayment: undefined,
  isPartialPayment: false,
  payUrl: undefined,
  stage: 'FULL',
  showPayButton: false,
  stageStatusName: null as string | null,
  isCheckButtonVisible: false,
  createdDate: '',
  expireDate: '',
  isOfferActive: false,
  isDeclined: false,
}

const checkButtonStatus = ['GRADE_WAITING', 'FIXING', 'COMPLETED']

const getPaymentStage = (
  offer: TServiceOffer,
  appealStatus: AppealStatus | null | undefined,
  isPartialPayment: boolean,
): TOfferPaymentStage => {
  if (!isPartialPayment) return 'FULL'

  const { acquiringSecondUrl, payments } = offer
  const isOfferPaid = appealStatus === 'OFFER_PAID'
  const firstPaymentMade = payments.some(p => p.isPaid)

  if (!acquiringSecondUrl) {
    if (!firstPaymentMade) return 'AWAITING_FIRST_PAYMENT'
    if (appealStatus === 'AWAITING_PAYMENT') return 'AWAITING_SECOND_LINK'
    return 'SETTLED'
  }

  if (!isOfferPaid && appealStatus === 'AWAITING_PAYMENT') {
    return 'AWAITING_SECOND_PAYMENT'
  }
  return 'SETTLED'
}

export const useOfferPriceBlock = (
  offer: TServiceOffer | null | undefined,
  appealStatus: AppealStatus | null | undefined,
  status?: TAppealOffer['status'],
  hasApprovedOrder?: boolean,
) => {
  if (!offer) return defaultResult

  const { acquiringUrl, acquiringSecondUrl, isFullPrepayment, payments } = offer

  const createdDate = getTimeByTimeZone(offer.createdDate)
  const expireDate = getTimeByTimeZone(offer.expireDate)
  const isOfferActive = checkIfDateLessTodayWithTime(expireDate, -1)

  // единая формула "отклонено" для КП в любом статусе (Approved передаёт
  // status='APPROVED' и hasApprovedOrder не задан, поэтому для него условие
  // сводится к проверке appealStatus, как и раньше)
  const isDeclined =
    status === 'DECLAINED' ||
    (status === 'SENT' && !!hasApprovedOrder) ||
    appealStatus === 'CANCELED' ||
    appealStatus === 'UNKNOWN'

  const unpaidPayment = payments.find(p => !p.isPaid)

  const isPartialPayment = !!acquiringUrl?.length && !isFullPrepayment
  const payUrl = acquiringSecondUrl || acquiringUrl

  const stage = getPaymentStage(offer, appealStatus, isPartialPayment)

  const showPayButton =
    stage === 'AWAITING_FIRST_PAYMENT' || stage === 'AWAITING_SECOND_PAYMENT'

  const stageStatusName =
    stage === 'AWAITING_SECOND_LINK'
      ? 'Заказ ожидает готовности'
      : stage === 'AWAITING_SECOND_PAYMENT'
      ? 'Заказ ожидает доплаты'
      : null

  const isCheckButtonVisible =
    checkButtonStatus.includes(appealStatus || '') && offer.checks.length > 0

  return {
    unpaidPayment,
    isPartialPayment,
    payUrl,
    stage,
    showPayButton,
    stageStatusName,
    isCheckButtonVisible,
    createdDate,
    expireDate,
    isOfferActive,
    isDeclined,
  }
}
